import {
  Button,
  ButtonTargetKind,
  ButtonSize,
  ButtonRole,
} from "@components/Button";
import { ErrorMessage } from "@components/ErrorMessage";
import { Icon, IconSize, IconDisplay } from "@components/Icon";
import { css } from "@emotion/react";
import type { CSSInterpolation } from "@emotion/serialize";
import React from "react";
import DatePicker, { CalendarContainer } from "react-datepicker";

import { colorPalette } from "@every.org/common/src/display/palette";

import { adminInputStyles } from "src/components/Admin/DonationsTab/styles";
import { colorCssVars } from "src/theme/color";
import { horizontalStackCss, verticalStackCss } from "src/theme/spacing";
import { textSizeCss } from "src/theme/text";

const styledDateInput = css`
  ${adminInputStyles};
`;

const datePickerInputCss = css`
  ${adminInputStyles};
  ${textSizeCss.xs};
  height: 40px;
  padding-top: 9px;
  padding-left: 12px;
`;

const datePickerCss = css`
  display: inline-block;
  font-size: 1em;
  color: ${colorPalette.teal};
  cursor: pointer;
  .react-datepicker {
    ${verticalStackCss.l}
    padding: 24px;
    border: none;
    box-shadow: 0px 8px 32px rgba(46, 52, 52, 0.2);
    border-radius: 8px;
  }
  .react-datepicker-wrapper {
    width: 100%;
  }
  .react-datepicker__header {
    background: none;
    border-bottom: none;
  }

  .react-datepicker__year-read-view--down-arrow,
  .react-datepicker__month-read-view--down-arrow,
  .react-datepicker__month-year-read-view--down-arrow,
  .react-datepicker__navigation-icon::before {
    border-color: ${colorPalette.green} !important;
  }

  .react-datepicker__day--keyboard-selected,
  .react-datepicker__month-text--keyboard-selected,
  .react-datepicker__quarter-text--keyboard-selected,
  .react-datepicker__year-text--keyboard-selected {
    background: ${colorPalette.green};
    color: ${colorPalette.white} !important;
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--in-range,
  .react-datepicker__month-text--selected,
  .react-datepicker__month-text--in-selecting-range,
  .react-datepicker__month-text--in-range,
  .react-datepicker__quarter-text--selected,
  .react-datepicker__quarter-text--in-selecting-range,
  .react-datepicker__quarter-text--in-range,
  .react-datepicker__year-text--selected,
  .react-datepicker__year-text--in-selecting-range,
  .react-datepicker__year-text--in-range {
    background: ${colorPalette.green};
  }

  .react-datepicker__day:hover,
  .react-datepicker__month-text:hover,
  .react-datepicker__quarter-text:hover,
  .react-datepicker__year-text:hover {
    color: ${colorPalette.green};
  }

  .react-datepicker__day--selected:hover,
  .react-datepicker__day--in-selecting-range:hover,
  .react-datepicker__day--in-range:hover,
  .react-datepicker__month-text--selected:hover,
  .react-datepicker__month-text--in-selecting-range:hover,
  .react-datepicker__month-text--in-range:hover,
  .react-datepicker__quarter-text--selected:hover,
  .react-datepicker__quarter-text--in-selecting-range:hover,
  .react-datepicker__quarter-text--in-range:hover,
  .react-datepicker__year-text--selected:hover,
  .react-datepicker__year-text--in-selecting-range:hover,
  .react-datepicker__year-text--in-range:hover {
    color: ${colorPalette.white};
  }
`;

type DatePickerInputProps = {
  onClick?: () => void;
  date: [Date | null, Date | null] | null[] | Date[];
  contentCss?: CSSInterpolation;
  placeholderText?: string;
  validationErrors?: string;
  className?: string;
};

const DatePickerCustomInput = React.forwardRef<
  HTMLButtonElement,
  DatePickerInputProps
>(
  (
    { onClick, date, contentCss, placeholderText, validationErrors, className },
    ref
  ) => {
    const startDate = date[0]
      ? `${date[0].toLocaleString("default", {
          month: "short",
        })} ${date[0].getDate()}`
      : "";
    const endDate = date[1]
      ? `${date[1].toLocaleString("default", {
          month: "short",
        })} ${date[1].getDate()}`
      : null;

    return (
      <React.Fragment>
        {onClick && (
          <Button
            className={className}
            css={[
              styledDateInput,
              contentCss,
              validationErrors &&
                css`
                  border: 1px solid var(${colorCssVars.input.border.error});
                `,
            ]}
            role={ButtonRole.UNSTYLED}
            data-tname="applyTableFilter"
            onClick={{
              kind: ButtonTargetKind.FUNCTION,
              action: onClick,
            }}
            size={ButtonSize.SMALL}
          >
            <div css={[horizontalStackCss.xs, { alignItems: "center" }]}>
              <Icon
                iconImport={() =>
                  import("src/components/Icon/icons/CalendarIcon")
                }
                display={IconDisplay.CURRENT_COLOR}
                css={{ position: "relative", bottom: "1px" }}
                size={IconSize.X_SMALL}
              />
              <div>
                {startDate || endDate
                  ? `${startDate ? startDate : ""} - ${endDate ? endDate : ""}`
                  : placeholderText || "Filter date"}
              </div>
            </div>
          </Button>
        )}
      </React.Fragment>
    );
  }
);

DatePickerCustomInput.displayName = "DatePickerCustomInput";

const ref = React.createRef<HTMLButtonElement>();

const DatePickerCalendar: React.FCC<{
  setDateRange: (date: [Date | null, Date | null] | null[] | Date[]) => void;
  dateRange: [Date | null, Date | null] | null[] | Date[];
  setFilterDateOption?: (option: string) => void;
  contentCss?: CSSInterpolation;
  placeholder?: string;
  validationErrors?: string;
  inputCss?: CSSInterpolation;
}> = ({
  dateRange,
  setDateRange,
  setFilterDateOption,
  contentCss,
  placeholder,
  validationErrors,
  inputCss,
}) => {
  const [startDate, endDate] = dateRange;
  const MyContainer = ({
    children,
    className,
  }: {
    children: React.ReactNode[];
    className: string;
  }) => {
    return (
      <div css={{ color: "#fff" }}>
        <CalendarContainer className={className}>
          <div
            css={[
              horizontalStackCss.s,
              { justifyContent: "space-between", alignItems: "center" },
            ]}
          >
            <div css={datePickerInputCss}>
              {startDate ? new Date(startDate).toLocaleDateString() : ""}
            </div>
            <Icon
              iconImport={() =>
                import("@components/Icon/icons/ArrowForwardIcon")
              }
              size={IconSize.SMALL}
              display={IconDisplay.CURRENT_COLOR}
              css={{ color: colorPalette.grey }}
            />
            <div css={datePickerInputCss}>
              {endDate ? new Date(endDate).toLocaleDateString() : ""}
            </div>
          </div>
          <div style={{ position: "relative" }}>{children}</div>
        </CalendarContainer>
      </div>
    );
  };

  return (
    <div css={verticalStackCss.s}>
      <div css={datePickerCss}>
        <DatePicker
          selectsRange
          startDate={startDate}
          endDate={endDate}
          onChange={(update) => {
            setDateRange(
              update as [Date | null, Date | null] | null[] | Date[]
            );
            setFilterDateOption && setFilterDateOption("Custom");
          }}
          dateFormat="MM/dd/yyyy"
          calendarContainer={MyContainer}
          customInput={
            <DatePickerCustomInput
              css={inputCss}
              placeholderText={placeholder}
              ref={ref}
              date={dateRange}
              contentCss={contentCss}
              validationErrors={validationErrors}
            />
          }
        />
      </div>
      {validationErrors && <ErrorMessage>{validationErrors}</ErrorMessage>}
    </div>
  );
};
export default DatePickerCalendar;
